import React, { useState, useEffect, useMemo } from 'react';
import { 
    Search, Filter, Clock, CheckCircle2, XCircle, 
    ChevronRight, FileText, User, Calendar, Info,
    Construction, Car, Bike, MoreHorizontal, Trash2
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { fetchRegistrations, processRegistrationAction, deleteRegistrationMedia } from '../../../services/registrationService';
import { ServiceRegistration, RegistrationStatus, Unit, Owner } from '../../../types';
import { useNotification } from '../../../App';

interface AdminPortalServiceRegistrationPageProps {
    units: Unit[];
    owners: Owner[];
}

const AdminPortalServiceRegistrationPage: React.FC<AdminPortalServiceRegistrationPageProps> = ({ units, owners }) => {
    const [registrations, setRegistrations] = useState<ServiceRegistration[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState<RegistrationStatus | 'All'>('All');
    const [selectedReg, setSelectedReg] = useState<ServiceRegistration | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [adminNote, setAdminNote] = useState('');
    const { showToast } = useNotification();

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await fetchRegistrations();
            setRegistrations(data);
        } catch (error) {
            showToast('Không thể tải danh sách đăng ký', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const filteredRegs = useMemo(() => {
        return registrations.filter(reg => {
            const matchesSearch = reg.residentId.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesStatus = filterStatus === 'All' || reg.status === filterStatus;
            return matchesSearch && matchesStatus;
        });
    }, [registrations, searchQuery, filterStatus]);

    const stats = useMemo(() => {
        return {
            pending: registrations.filter(r => r.status === 'Pending').length,
            approved: registrations.filter(r => r.status === 'Approved').length,
            rejected: registrations.filter(r => r.status === 'Rejected').length,
        };
    }, [registrations]);

    const handleAction = async (status: RegistrationStatus) => {
        if (!selectedReg) return;
        setIsProcessing(true);
        try {
            // In a real app, we'd get the admin email and name from context
            const adminEmail = 'admin@example.com'; 
            const adminName = 'Quản trị viên';
            
            await processRegistrationAction(
                selectedReg.id, 
                status, 
                adminNote, 
                adminEmail, 
                selectedReg.residentId,
                adminName
            );
            
            showToast(`Đã ${status === 'Approved' ? 'duyệt' : 'từ chối'} yêu cầu`, 'success');
            setSelectedReg(null);
            setAdminNote('');
            loadData();
        } catch (error) {
            showToast('Lỗi khi xử lý yêu cầu', 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDeleteMedia = async (regId: string) => {
        if (!window.confirm('Bạn có chắc chắn muốn xoá tài liệu đính kèm để tiết kiệm dung lượng?')) return;
        try {
            await deleteRegistrationMedia(regId);
            showToast('Đã xoá tài liệu đính kèm', 'success');
            loadData();
            if (selectedReg?.id === regId) {
                setSelectedReg(prev => prev ? { ...prev, documents: [] } : null);
            }
        } catch (error) {
            showToast('Lỗi khi xoá tài liệu', 'error');
        }
    };

    const getStatusColor = (status: RegistrationStatus) => {
        switch (status) {
            case 'Pending': return 'bg-amber-100 text-amber-700 border-amber-200';
            case 'Approved': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
            case 'Rejected': return 'bg-red-100 text-red-700 border-red-200';
            default: return 'bg-gray-100 text-gray-700 border-gray-200';
        }
    };

    const getStatusIcon = (status: RegistrationStatus) => {
        switch (status) {
            case 'Pending': return <Clock className="w-3 h-3" />;
            case 'Approved': return <CheckCircle2 className="w-3 h-3" />;
            case 'Rejected': return <XCircle className="w-3 h-3" />;
            default: return null;
        }
    };

    const getRegTypeIcon = (type: string) => {
        return type === 'Construction' ? <Construction className="w-5 h-5" /> : <Car className="w-5 h-5" />;
    };

    return (
        <div className="flex flex-col h-full bg-gray-50 pb-20">
            {/* Header Stats */}
            <div className="grid grid-cols-3 gap-2 p-4 bg-white border-b sticky top-0 z-10">
                <div className="bg-amber-50 p-3 rounded-xl border border-amber-100 flex flex-col items-center">
                    <span className="text-amber-600 font-bold text-lg">{stats.pending}</span>
                    <span className="text-[10px] text-amber-700 uppercase font-bold tracking-wider">Chờ duyệt</span>
                </div>
                <div className="bg-emerald-50 p-3 rounded-xl border border-emerald-100 flex flex-col items-center">
                    <span className="text-emerald-600 font-bold text-lg">{stats.approved}</span>
                    <span className="text-[10px] text-emerald-700 uppercase font-bold tracking-wider">Đã duyệt</span>
                </div>
                <div className="bg-red-50 p-3 rounded-xl border border-red-100 flex flex-col items-center">
                    <span className="text-red-600 font-bold text-lg">{stats.rejected}</span>
                    <span className="text-[10px] text-red-700 uppercase font-bold tracking-wider">Từ chối</span>
                </div>
            </div>

            {/* Search & Filter */}
            <div className="p-4 space-y-3">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input 
                        type="text"
                        placeholder="Tìm theo số căn..."
                        className="w-full pl-10 pr-4 py-2.5 bg-white border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                    {['All', 'Pending', 'Approved', 'Rejected'].map((s) => (
                        <button
                            key={s}
                            onClick={() => setFilterStatus(s as any)}
                            className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap border transition-all ${
                                filterStatus === s 
                                ? 'bg-blue-600 text-white border-blue-600 shadow-md' 
                                : 'bg-white text-gray-600 border-gray-200'
                            }`}
                        >
                            {s === 'All' ? 'Tất cả' : s === 'Pending' ? 'Chờ duyệt' : s === 'Approved' ? 'Đã duyệt' : 'Từ chối'}
                        </button>
                    ))}
                </div>
            </div>

            {/* List */}
            <div className="flex-1 px-4 space-y-3 overflow-y-auto">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 space-y-3">
                        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm text-gray-500 font-medium">Đang tải dữ liệu...</span>
                    </div>
                ) : filteredRegs.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-300">
                        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 font-medium">Không tìm thấy yêu cầu nào</p>
                    </div>
                ) : (
                    filteredRegs.map((reg) => (
                        <motion.div
                            key={reg.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            onClick={() => setSelectedReg(reg)}
                            className="bg-white p-4 rounded-2xl border shadow-sm active:scale-[0.98] transition-all"
                        >
                            <div className="flex justify-between items-start mb-3">
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-xl ${reg.type === 'Construction' ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600'}`}>
                                        {getRegTypeIcon(reg.type)}
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-900">Căn {reg.residentId}</h3>
                                        <p className="text-[11px] text-gray-500 font-medium">{reg.type === 'Construction' ? 'Đăng ký thi công' : 'Đăng ký phương tiện'}</p>
                                    </div>
                                </div>
                                <div className={`px-2 py-1 rounded-lg border text-[10px] font-bold flex items-center gap-1 ${getStatusColor(reg.status)}`}>
                                    {getStatusIcon(reg.status)}
                                    {reg.status === 'Pending' ? 'CHỜ DUYỆT' : reg.status === 'Approved' ? 'ĐÃ DUYỆT' : 'TỪ CHỐI'}
                                </div>
                            </div>

                            <div className="flex items-center justify-between text-[11px] text-gray-500 border-t pt-3">
                                <div className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {new Date(reg.date).toLocaleDateString('vi-VN')}
                                </div>
                                <div className="flex items-center gap-1 font-bold text-blue-600">
                                    Chi tiết <ChevronRight className="w-3 h-3" />
                                </div>
                            </div>
                        </motion.div>
                    ))
                )}
            </div>

            {/* Detail Bottom Sheet */}
            <AnimatePresence>
                {selectedReg && (
                    <>
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelectedReg(null)}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
                        />
                        <motion.div 
                            initial={{ y: '100%' }}
                            animate={{ y: 0 }}
                            exit={{ y: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[32px] z-[70] max-h-[90vh] overflow-y-auto shadow-2xl"
                        >
                            <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto my-4" />
                            
                            <div className="px-6 pb-8 space-y-6">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h2 className="text-2xl font-black text-gray-900">Chi tiết đăng ký</h2>
                                        <p className="text-sm text-gray-500 font-medium">Mã yêu cầu: #{selectedReg.id.slice(-6).toUpperCase()}</p>
                                    </div>
                                    <div className={`px-3 py-1.5 rounded-xl border text-xs font-bold flex items-center gap-1.5 ${getStatusColor(selectedReg.status)}`}>
                                        {getStatusIcon(selectedReg.status)}
                                        {selectedReg.status === 'Pending' ? 'Chờ duyệt' : selectedReg.status === 'Approved' ? 'Đã duyệt' : 'Từ chối'}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-gray-50 p-4 rounded-2xl border">
                                        <div className="flex items-center gap-2 text-gray-400 mb-1">
                                            <User className="w-3.5 h-3.5" />
                                            <span className="text-[10px] font-bold uppercase tracking-wider">Căn hộ</span>
                                        </div>
                                        <p className="font-bold text-gray-900">{selectedReg.residentId}</p>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-2xl border">
                                        <div className="flex items-center gap-2 text-gray-400 mb-1">
                                            <Calendar className="w-3.5 h-3.5" />
                                            <span className="text-[10px] font-bold uppercase tracking-wider">Ngày gửi</span>
                                        </div>
                                        <p className="font-bold text-gray-900">{new Date(selectedReg.date).toLocaleDateString('vi-VN')}</p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h3 className="font-bold text-gray-900 flex items-center gap-2">
                                        <Info className="w-4 h-4 text-blue-600" />
                                        Thông tin chi tiết
                                    </h3>
                                    <div className="bg-white border rounded-2xl overflow-hidden">
                                        {selectedReg.type === 'Construction' ? (
                                            <div className="divide-y">
                                                <div className="p-4 flex justify-between">
                                                    <span className="text-sm text-gray-500">Hạng mục</span>
                                                    <span className="text-sm font-bold text-gray-900">{selectedReg.details.constructionItem}</span>
                                                </div>
                                                <div className="p-4 flex justify-between">
                                                    <span className="text-sm text-gray-500">Thời gian</span>
                                                    <span className="text-sm font-bold text-gray-900">{selectedReg.details.constructionTime}</span>
                                                </div>
                                                <div className="p-4 flex justify-between">
                                                    <span className="text-sm text-gray-500">Nhà thầu</span>
                                                    <span className="text-sm font-bold text-gray-900">{selectedReg.details.contractor}</span>
                                                </div>
                                                <div className="p-4">
                                                    <span className="text-sm text-gray-500 block mb-2">Mô tả chi tiết</span>
                                                    <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-xl">{selectedReg.details.description || 'Không có mô tả'}</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="divide-y">
                                                <div className="p-4 flex justify-between">
                                                    <span className="text-sm text-gray-500">Loại xe</span>
                                                    <span className="text-sm font-bold text-gray-900">{selectedReg.details.vehicleType === 'car' ? 'Ô tô' : 'Xe máy'}</span>
                                                </div>
                                                <div className="p-4 flex justify-between">
                                                    <span className="text-sm text-gray-500">Biển số</span>
                                                    <span className="text-sm font-bold text-gray-900">{selectedReg.details.plate}</span>
                                                </div>
                                                <div className="p-4 flex justify-between">
                                                    <span className="text-sm text-gray-500">Nhãn hiệu/Model</span>
                                                    <span className="text-sm font-bold text-gray-900">{selectedReg.details.model}</span>
                                                </div>
                                                <div className="p-4 flex justify-between">
                                                    <span className="text-sm text-gray-500">Màu sắc</span>
                                                    <span className="text-sm font-bold text-gray-900">{selectedReg.details.color}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {selectedReg.documents.length > 0 && (
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center">
                                            <h3 className="font-bold text-gray-900 flex items-center gap-2">
                                                <FileText className="w-4 h-4 text-blue-600" />
                                                Tài liệu đính kèm
                                            </h3>
                                            <button 
                                                onClick={() => handleDeleteMedia(selectedReg.id)}
                                                className="text-red-500 p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            {selectedReg.documents.map((doc, idx) => (
                                                <a 
                                                    key={idx}
                                                    href={doc.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-2 p-3 bg-blue-50 text-blue-700 rounded-xl border border-blue-100 text-xs font-bold"
                                                >
                                                    <FileText className="w-4 h-4" />
                                                    <span className="truncate">{doc.name}</span>
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedReg.status === 'Pending' ? (
                                    <div className="space-y-4 pt-4 border-t">
                                        <div>
                                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 block">Ghi chú xử lý / Lý do từ chối</label>
                                            <textarea 
                                                value={adminNote}
                                                onChange={(e) => setAdminNote(e.target.value)}
                                                placeholder="Nhập ghi chú phản hồi cho cư dân..."
                                                className="w-full p-4 bg-gray-50 border rounded-2xl text-sm focus:ring-2 focus:ring-blue-500 outline-none min-h-[100px]"
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <button
                                                disabled={isProcessing}
                                                onClick={() => handleAction('Rejected')}
                                                className="py-4 bg-red-50 text-red-600 rounded-2xl font-bold border border-red-100 active:scale-95 transition-all disabled:opacity-50"
                                            >
                                                Từ chối
                                            </button>
                                            <button
                                                disabled={isProcessing}
                                                onClick={() => handleAction('Approved')}
                                                className="py-4 bg-blue-600 text-white rounded-2xl font-bold shadow-lg shadow-blue-200 active:scale-95 transition-all disabled:opacity-50"
                                            >
                                                Duyệt yêu cầu
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="p-4 bg-gray-50 rounded-2xl border space-y-2">
                                        <div className="flex justify-between text-xs">
                                            <span className="text-gray-500">Người xử lý:</span>
                                            <span className="font-bold text-gray-900">{selectedReg.processedBy}</span>
                                        </div>
                                        <div className="flex justify-between text-xs">
                                            <span className="text-gray-500">Thời gian:</span>
                                            <span className="font-bold text-gray-900">{selectedReg.processedAt ? new Date(selectedReg.processedAt).toLocaleString('vi-VN') : '---'}</span>
                                        </div>
                                        {selectedReg.rejectionReason && (
                                            <div className="pt-2 border-t mt-2">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Nội dung phản hồi:</span>
                                                <p className="text-sm text-gray-700 italic">"{selectedReg.rejectionReason}"</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <button 
                                    onClick={() => setSelectedReg(null)}
                                    className="w-full py-4 text-gray-500 font-bold text-sm"
                                >
                                    Đóng
                                </button>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminPortalServiceRegistrationPage;
